import cn from 'classnames'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FunctionComponent, useEffect, useState } from 'react'

import { Button } from '@/components/Button'
import { Icon } from '@/icons'
import { mainStore } from '@/domains/Account/store'
import { MktuClassStore } from '@/store/Mktu/MktuClass'

import { MktuClassPopup } from '../MktuClassPopup'
import { MktuClassInfo } from './components/MktuClassInfo'
import { MktuClassList } from './components/MktuClassList'
import { MktuManualSelectStore } from './MktuManualSelect.store'

import styles from './MktuManualSelect.module.scss'

interface IMktuManualSelectProps {
  selected: MktuClassStore['number'][]
  onChange: (values: MktuClassStore['number'][]) => void
  className?: string
  isVisible?: boolean
  withHeader?: boolean
  withControl?: boolean
  withDescription?: boolean
  onClose?: () => void
  title?: string
}
export const MktuManualSelect: FunctionComponent<IMktuManualSelectProps> =
  observer(
    ({
      className,
      onChange,
      selected,
      isVisible = true,
      withHeader = true,
      withControl = false,
      withDescription = false,
      onClose,
      title = 'Выбрать класс МКТУ',
    }) => {
      const [store] = useState(() => new MktuManualSelectStore())

      useEffect(() => {
        store.setSelected(selected)
      }, [store, selected])

      useEffect(
        () => reaction(() => store.selected, onChange),
        [onChange, store.selected]
      )

      useEffect(() => {
        if (!isVisible) {
          store.disableModeClassesDetails()
        }
      }, [isVisible, store])

      useEffect(() => {
        mainStore.mktuClassesList.loader.request()
      }, [])

      return (
        <div
          className={cn(className, styles.container, {
            [styles.isHidden]: !isVisible,
          })}
        >
          {withHeader ? (
            <div
              className={cn(styles.mainHeader, {
                [styles.isDetails]: store.isModeClassesDetailsEnabled,
              })}
            >
              <div className={styles.title}>{title}</div>
              {onClose != null ? (
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                >
                  <Icon name="close" />
                </button>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(styles.content, {
              [styles.isDetails]: store.isModeClassesDetailsEnabled,
            })}
          >
            <div
              className={cn(styles.left, {
                [styles.isDetails]: store.isModeClassesDetailsEnabled,
              })}
            >
              {withControl ? (
                <div
                  className={cn(styles.header, {
                    [styles.isDetails]: store.isModeClassesDetailsEnabled,
                  })}
                >
                  {!store.isModeClassesDetailsEnabled ? (
                    <Button
                      type="button"
                      variant={selected.length !== 0 ? 'tertiary' : 'primary'}
                      size="small"
                      name="Все классы"
                      onClick={store.selectAll}
                      testId="mktuPopupSelectAll"
                    />
                  ) : null}

                  <button
                    type="button"
                    className={cn(styles.buttonClassDetails, {
                      [styles.isBack]: store.isModeClassesDetailsEnabled,
                    })}
                    onClick={store.toggleModeClassesDetails}
                  >
                    {store.isModeClassesDetailsEnabled
                      ? 'Назад'
                      : 'Подробнее о классах'}
                  </button>
                </div>
              ) : null}

              {!store.isModeClassesDetailsEnabled ? (
                <MktuClassList
                  selected={selected}
                  onSelect={store.selectByClassNumber}
                  onMouseEnter={store.showMktuClassInfo}
                  onMouseLeave={store.hideMktuClassInfo}
                />
              ) : null}

              {store.isModeClassesDetailsEnabled ? (
                <div
                  className={cn(styles.classDetailsDescription, {
                    [styles.isDetails]: store.isModeClassesDetailsEnabled,
                  })}
                >
                  Нажмите на класс, чтобы посмотреть подробную информацию
                </div>
              ) : null}
            </div>

            {withDescription ? (
              <div
                className={cn(styles.right, {
                  [styles.isDetails]: store.isModeClassesDetailsEnabled,
                })}
              >
                {!store.isModeClassesDetailsEnabled ? (
                  <MktuClassInfo
                    visibleInfo={store.visibleInfoClassNumber != null}
                    classNumber={store.visibleInfoClassNumber}
                  />
                ) : null}

                {store.isModeClassesDetailsEnabled ? (
                  <MktuClassList
                    selected={selected}
                    onSelect={store.setMktuClassNumberForDetails}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {store.mktuClassNumberForDetails != null ? (
            <div className={styles.mktuClassPopup} data-testid="mktuPopup">
              <MktuClassPopup
                mktuClass={store.mktuClassForDetails}
                onClose={store.hideMktuClassDetails}
                onSelectMktuClass={store.select}
                isSelectedClass={store.selected.includes(
                  store.mktuClassForDetails.number
                )}
                isReadonly
              />
            </div>
          ) : null}
        </div>
      )
    }
  )
